<template>
    <section class="search-page-top">
        <div class="content">
            <PrismicRichText v-if="searchDocument" :field="searchDocument.data.pageTitle" class="title" />
            <VJoyInput name="search" icon="search" class="input" :model-value="search" @update:model-value="(value) => (search = value)" />
            <div role="tablist" class="content-type-filters">
                <button
                    v-for="contentType in contentTypesOrdered"
                    :id="contentType"
                    :key="contentType"
                    role="tab"
                    :aria-controls="`${contentType}-results`"
                    :aria-selected="contentTypeFilter === contentType"
                    :class="{'content-type-selector': true, selected: contentTypeFilter === contentType}"
                    :disabled="results[contentType].length === 0"
                    @click="() => (contentTypeFilter = contentType)"
                >
                    {{ t(`resources.${contentType === 'successStory' ? 'successStories' : (`${contentType}s` as const)}`) }} ({{
                        results[contentType].length
                    }})
                </button>
            </div>
        </div>
    </section>
    <section class="search-page-results">
        <div class="content>">
            <CategorySelector class="categories-filters" />
            <div
                v-for="contentType in contentTypesOrdered"
                :id="`${contentType}-results`"
                :key="contentType"
                :aria-labelledby="contentType"
                role="tabpanel"
                :tabindex="contentTypeFilter === contentType ? 0 : -1"
                :hidden="contentTypeFilter !== contentType"
                class="results-grid"
            >
                <template v-if="loading">
                    <PlaceholderCard v-for="i in 5" :key="i" />
                </template>
                <template v-else-if="results[contentType].length">
                    <template v-for="(doc, index) of results[contentType]" :key="doc.uid ?? ''">
                        <ArticleCard v-if="doc.type === 'article' || doc.type === 'successStory'" :article="doc" />
                        <GuideCard v-else-if="doc.type === 'guide'" :guide="doc" />
                        <EventCard v-else-if="doc.type === 'event'" :event="doc" />
                        <CTACard
                            v-if="searchDocument?.data.title && searchDocument.data.ctaLink && searchDocument.data.ctaText && index === 4"
                            class="cta"
                            :title="searchDocument.data.title"
                            :text="searchDocument.data.text"
                            :cta-link="searchDocument.data.ctaLink"
                            :cta-text="searchDocument.data.ctaText"
                        />
                    </template>
                    <CTACard
                        v-if="
                            searchDocument?.data.title &&
                            searchDocument.data.ctaLink &&
                            searchDocument.data.ctaText &&
                            results[contentType].length < 5
                        "
                        class="cta"
                        :title="searchDocument.data.title"
                        :text="searchDocument.data.text"
                        :cta-link="searchDocument.data.ctaLink"
                        :cta-text="searchDocument.data.ctaText"
                    />
                </template>
            </div>
            <p v-if="noResults" class="text-center joy-mt-3 joy-color-neutral-40">{{ t('generic.no.results.found') }}</p>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import {computed, ref, watch} from 'vue';
    import {ParsingError} from '@prismicio/client';
    import {PrismicRichText} from '@prismicio/vue';
    import type {
        ArticleDocument,
        EventDocument,
        GuideDocument,
        ResourceCategoryDocument,
        ResourcesCenterSearchDocument,
        SuccessStoryDocument,
    } from '@prismicio-types';
    import {useRoute} from '@typed-router';
    import {useAsyncData, useTranslation, useWebsitePageView} from '#imports';
    import {VJoyInput} from '@maltjoy/core-vue';
    import type {CATEGORIES} from '~/app/keys';
    import ArticleCard from '~/components/ArticleCard.vue';
    import CategorySelector from '~/components/CategorySelector.vue';
    import CTACard from '~/components/CTACard.vue';
    import EventCard from '~/components/EventCard.vue';
    import GuideCard from '~/components/GuideCard.vue';
    import PlaceholderCard from '~/components/PlaceholderCard.vue';
    import usePrismicDocument from '~/composables/content/usePrismicDocument';
    import {useResolvePrismicDocument} from '~/composables/content/useResolvePrismicDocument';
    import {useDocumentMetadata} from '~/composables/useDocumentMetadata';
    import {baseSearchPredicatesByCustomType, type SearchCustomType, type SearchParams} from '~/utils/searchPredicates';
    import usePrismicSearch from '~~/app/composables/content/usePrismicSearch';

    // Composables
    const {data: searchDocumentData, error: searchDocumentError} = await useAsyncData('resources-center-search', () =>
        usePrismicDocument<ResourcesCenterSearchDocument>('resourcesCenterSearch'),
    );
    const searchDocument = useResolvePrismicDocument(searchDocumentData, searchDocumentError);
    const {query} = useRoute();
    const {t} = useTranslation();
    useWebsitePageView('resources-center', 'search');

    // Ref values
    const categoryFilter = ref<(typeof CATEGORIES)[number] | undefined>();
    const loading = ref(false);
    const contentTypeFilter = ref<SearchCustomType | undefined>('article');
    const categoryId = ref<string | undefined>();
    const queryParamResourceCategory = query.category ? (query.category as string) : undefined;
    const search = ref(typeof query.search === 'string' ? decodeURIComponent(query.search) : '');
    const searchTimeout = ref<NodeJS.Timeout>();
    const results = ref<Record<SearchCustomType, ArticleDocument[] | GuideDocument[] | EventDocument[] | SuccessStoryDocument[]>>({
        article: [],
        guide: [],
        event: [],
        successStory: [],
    });

    const noResults = computed(() => {
        return Object.values(results.value).every((result) => result.length === 0);
    });

    const contentTypesOrdered: SearchCustomType[] = ['article', 'guide', 'event', 'successStory'];

    watch([search, categoryFilter, categoryId], () => {
        if (searchTimeout.value) {
            clearTimeout(searchTimeout.value);
        }
        searchTimeout.value = setTimeout(() => {
            performSearch(true);
        }, 500);
    });

    async function searchCategoryId() {
        loading.value = true;
        const {data: resourceCategoryResult} = await useAsyncData(
            'resource-categories-search-filtered',
            async () => {
                if (queryParamResourceCategory) {
                    return await usePrismicSearch<ResourceCategoryDocument>({
                        type: 'resourceCategory',
                        uid: queryParamResourceCategory,
                    });
                }
            },
            {deep: true},
        );
        categoryId.value = resourceCategoryResult.value?.results[0]?.id;
        if (!categoryId.value) {
            categoryFilter.value = typeof query.category === 'string' ? (query.category as (typeof CATEGORIES)[number]) : undefined;
        }
        loading.value = false;
    }

    async function performContentSearch<T extends ArticleDocument | GuideDocument | EventDocument | SuccessStoryDocument>(
        params: SearchParams,
        onDemand: boolean,
    ) {
        try {
            return await usePrismicSearch<T>(params, onDemand);
        } catch (error) {
            if (error instanceof ParsingError && error.message.includes('resourceCategory')) {
                return {results: []};
            }
            throw error;
        }
    }

    async function performSearch(onDemand = false) {
        loading.value = true;

        const searchParams: Record<SearchCustomType, SearchParams> = {
            article: buildSearchParams('article', baseSearchPredicatesByCustomType.article),
            guide: buildSearchParams('guide', baseSearchPredicatesByCustomType.guide),
            event: buildSearchParams('event', baseSearchPredicatesByCustomType.event),
            successStory: buildSearchParams('successStory', baseSearchPredicatesByCustomType.successStory),
        };

        const [articleResults, guideResults, eventResults, successStoryResults] = await Promise.all([
            performContentSearch<ArticleDocument>(searchParams.article, onDemand),
            performContentSearch<GuideDocument>(searchParams.guide, onDemand),
            performContentSearch<EventDocument>(searchParams.event, onDemand),
            performContentSearch<SuccessStoryDocument>(searchParams.successStory, onDemand),
        ]);

        results.value = {
            article: articleResults?.results ?? [],
            guide: guideResults?.results ?? [],
            event: eventResults?.results ?? [],
            successStory: successStoryResults?.results ?? [],
        };
        loading.value = false;
    }

    function buildSearchParams(type: SearchCustomType, customSearchParams?: Partial<SearchParams>): SearchParams {
        return {
            type,
            pageSize: 100,
            search: search.value,
            category: categoryFilter.value,
            resourceCategoryId: categoryId.value,
            ...customSearchParams,
        };
    }

    if (queryParamResourceCategory) {
        await searchCategoryId();
    } else {
        await performSearch();
    }

    if (searchDocument.value) {
        useDocumentMetadata(searchDocument.value);
    }
</script>
<style lang="scss">
    @use '~/assets/styles/variables' as *;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .search-page-top {
        border-bottom: solid 1px var(--joy-color-neutral-30);

        .content {
            padding-top: var(--joy-core-spacing-9);

            @include content-width;

            .title {
                margin-bottom: var(--joy-core-spacing-5);

                h1 {
                    font-size: 40px;

                    @media (max-width: $mobile-breakpoint) {
                        font-size: 30px;
                    }
                }
            }

            .joy-input--wrapper::after {
                display: none !important;
            }

            .content-type-filters {
                display: flex;
                align-items: flex-end;
                gap: var(--joy-core-spacing-6);
                overflow: auto;

                .content-type-selector {
                    position: relative;
                    padding-bottom: var(--joy-core-spacing-4);
                    font-weight: var(--joy-font-weight-bold);
                    color: var(--joy-color-neutral-40);

                    &.selected {
                        color: var(--joy-color-primary-50);

                        &::after {
                            content: '';
                            position: absolute;
                            bottom: 0;
                            left: 0;
                            width: 100%;
                            height: 4px;
                            border-radius: var(--joy-core-radius-2) var(--joy-core-radius-2) 0 0;
                            background-color: var(--joy-color-primary-50);
                        }
                    }

                    &:disabled {
                        opacity: 0.5;
                    }
                }
            }
        }
    }

    .search-page-results {
        padding-top: var(--joy-core-spacing-10);

        @include content-width;

        .categories-filters {
            display: flex;
            gap: var(--joy-core-spacing-3);
            margin-bottom: var(--joy-core-spacing-9);
            flex-wrap: wrap;
        }

        .results-grid {
            display: grid;
            row-gap: var(--joy-core-spacing-5);
            grid-template-columns: minmax(0, 1fr);

            @include mq.screen_sm(min) {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: var(--joy-core-spacing-1) var(--joy-core-spacing-3);
                margin-left: -15px;
                margin-right: -15px;
            }

            @include mq.screen_lg(min) {
                grid-template-columns: repeat(3, minmax(0, 1fr));
                gap: var(--joy-core-spacing-6) var(--joy-core-spacing-4);
            }

            .cta {
                justify-self: stretch;
            }
        }
    }
</style>
